import * as React from "react";
import {
  ListBox,
  ListBoxToolbar,
  processListBoxData,
  ListBoxToolbarClickEvent,
  ListBoxItemClickEvent,
} from "@progress/kendo-react-listbox";

import "./CPListBoxStyles.scss";
import { getFieldError, noop } from "../../../helpers";
import CPText from "shared/components/base/CPText/CPText";
import { Box } from "shared/components/base";
import CPTextBox from "../CPTextBox/CPTextBox";

export type CPBoxOption = {
  available: CPListBoxItem[];
  selected: CPListBoxItem[];
};

interface CPListBoxProps {
  /** Optional heading text rendered above the two list boxes. */
  headerText?: string;
  /** Object containing the `available` and `selected` item arrays. */
  boxOptions: CPBoxOption;
  /** Callback fired whenever items are moved between lists or their selection state changes. */
  onChange: (options: CPBoxOption) => void;
  /** Inline styles applied to each list box element. */
  boxStyles?: React.CSSProperties;
  filterable?: boolean;
  /** When true, at least `minRequiredSelection` items must be in the Selected list. */
  required?: boolean;
  /** Minimum number of selected items required when `required` is true. Defaults to 1. */
  minRequiredSelection?: number;
  /** Raw API error response `{ errors: [{ propertyName, errorMessage }] }`. Used with `name` to resolve a field-level error. */
  error?: any;
  /** Direct error message string; takes precedence over `error`. */
  errorMsg?: string;
  /** Field name used to look up a field-level error from the `error` object. */
  name?: string;
}

export interface CPListBoxItem {
  /** Display text for the item. */
  label: string;
  /** Underlying value of the item. */
  value: string | number;
  /** Whether the item is highlighted/selected within its list. */
  selected: boolean;
}

/**
 * Dual-list box (Available ↔ Selected) with toolbar controls for moving items between lists.
 * Wraps the Kendo `ListBox` with `processListBoxData` for reorder and transfer operations.
 *
 * @example
 * ```tsx
 * const [options, setOptions] = React.useState<CPBoxOption>({
 *   available: [{ label: 'Admin', value: 'admin', selected: false }],
 *   selected:  [{ label: 'User',  value: 'user',  selected: false }],
 * });
 *
 * <CPListBox
 *   headerText="Assign Roles"
 *   boxOptions={options}
 *   onChange={setOptions}
 * />
 * ```
 */
const CPListBox: React.FC<CPListBoxProps> = ({
  headerText,
  boxOptions,
  onChange = noop,
  boxStyles,
  filterable = false,
  required = false,
  minRequiredSelection = 1,
  error,
  errorMsg,
  name,
}) => {
  const [filterValue, setFilterValue] = React.useState("");
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (error != null) setDirty(false);
  }, [error]);

  const isValid =
    !required || boxOptions.selected.length >= minRequiredSelection;

  const resolvedError =
    errorMsg ?? getFieldError(dirty ? undefined : error, name ?? "");

  const handleItemClick = (
    event: ListBoxItemClickEvent,
    box: "available" | "selected",
  ) => {
    if (box === "available") {
      const mapped = boxOptions.available.map((item) => {
        if (item.label === event.dataItem.label) {
          item.selected = !item.selected;
        } else if (!event.nativeEvent.ctrlKey) {
          item.selected = false;
        }
        return item;
      });

      handleChange({
        available: mapped,
        selected: boxOptions.selected.map((item) => ({
          ...item,
          selected: false,
        })),
      });
    } else {
      const mapped = boxOptions.selected.map((item) => {
        if (item.label === event.dataItem.label) {
          item.selected = !item.selected;
        } else if (!event.nativeEvent.ctrlKey) {
          item.selected = false;
        }
        return item;
      });

      handleChange({
        available: boxOptions.available.map((item) => ({
          ...item,
          selected: false,
        })),
        selected: mapped,
      });
    }
  };

  const handleChange = (options: CPBoxOption) => {
    setDirty(true);
    onChange(options);
  };

  const handleToolBarClick = (e: ListBoxToolbarClickEvent) => {
    let toolName: string = e.toolName || "";
    let result: any = processListBoxData(
      boxOptions.available,
      boxOptions.selected,
      toolName,
      "selected",
    );

    const available = result.listBoxOneData.map((x: CPListBoxItem) => ({
      ...x,
      selected: false,
    }));
    const selected = result.listBoxTwoData.map((x: CPListBoxItem) => ({
      ...x,
      selected: false,
    }));

    handleChange({
      available,
      selected,
    });
  };

  const options = React.useMemo(() => {
    if (!filterValue || !filterable) return boxOptions;

    return {
      available: boxOptions.available.filter((item) =>
        item.label.toLowerCase().includes(filterValue.toLowerCase()),
      ),
      selected: boxOptions.selected.filter((item) =>
        item.label.toLowerCase().includes(filterValue.toLowerCase()),
      ),
    };
  }, [filterValue, filterable, boxOptions]);

  return (
    <div
      className={`cp-listbox-container${!isValid ? " cp-listbox-invalid" : ""}`}
    >
      <div className="cp-listbox-header">
        <CPText textStyle="subtitle">
          {headerText}
          {required && <span className="cp-listbox-required-star"> *</span>}
        </CPText>
      </div>

      {resolvedError != null && <CPText color="error">{resolvedError}</CPText>}
      {filterable && (
        <Box spacing={5} className="cp-listbox-filter">
          <CPTextBox
            name="filter"
            placeholder="Filter items..."
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value as string)}
          />
        </Box>
      )}
      <div className="cp-listboxes">
        <div className="cp-available-listbox">
          <CPText style={{ opacity: 0.6 }}>Available</CPText>
          <ListBox
            draggable={false}
            style={{
              height: 200,
              width: "100%",
              ...boxStyles,
              maxHeight: 400,
            }}
            data={options.available}
            textField="label"
            selectedField="selected"
            onItemClick={(e: ListBoxItemClickEvent) =>
              handleItemClick(e, "available")
            }
            toolbar={() => {
              return (
                <ListBoxToolbar
                  tools={[
                    "transferTo",
                    "transferFrom",
                    "transferAllTo",
                    "transferAllFrom",
                  ]}
                  data={options.available}
                  dataConnected={options.selected}
                  onToolClick={handleToolBarClick}
                />
              );
            }}
          />
          <Box spacing={5}>
            <CPText textStyle="caption" transform="uppercase">
              Hold CTRL + Click To Select Multiple
            </CPText>
          </Box>
        </div>
        <div className="cp-selected-listbox">
          <CPText style={{ opacity: 0.6 }}>Selected</CPText>
          <ListBox
            draggable={false}
            style={{
              height: 200,
              width: "100%",
              ...boxStyles,
              maxHeight: 400,
            }}
            data={options.selected}
            textField="label"
            selectedField="selected"
            onItemClick={(e: ListBoxItemClickEvent) =>
              handleItemClick(e, "selected")
            }
          />
        </div>
      </div>
      {required && !isValid && (
        <CPText textStyle="caption" color="error" style={{ marginTop: 6 }}>
          Required
          {minRequiredSelection > 1
            ? ` — at least ${minRequiredSelection} ${minRequiredSelection === 1 ? "item" : "items"} must be assigned`
            : " — at least one item must be assigned"}
        </CPText>
      )}
    </div>
  );
};

export default CPListBox;
